import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Image as ImageIcon,
  LayoutGrid,
  List,
  Loader2,
  Lock,
  MessageSquare,
  Reply
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../components/ui/table";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { identidadeDaRede } from "./redes";
import Cabecalho from "./Cabecalho";

// Comentários das publicações, respondidos daqui.
//
// Duas respostas diferentes, e a diferença importa: a pública fica embaixo do
// post, à vista de todo mundo; a privada abre uma conversa com quem comentou.
// Cobrança, endereço e dado de cliente pedem a privada.

const formatarData = (valor) => {
  if (!valor) return "";

  try {
    return new Date(valor).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return "";
  }
};

const SeloDeRede = ({ plataforma }) => {
  const rede = identidadeDaRede(plataforma);
  const Glifo = rede.icone;

  return (
    <span
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium"
      style={{ backgroundColor: `${rede.corSelo}1a`, color: rede.corSelo }}
    >
      <Glifo className="h-3 w-3" />
      {rede.rotulo}
    </span>
  );
};

// A conversa de um post. Fica em modal porque responder exige ler o
// comentário inteiro e escrever com calma — em linha, cada resposta empurrava
// a lista e fazia perder o lugar.
const ThreadModal = ({ publicacao, onClose }) => {
  const [comentarios, setComentarios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [respostas, setRespostas] = useState({});
  const [enviando, setEnviando] = useState(null);

  const carregar = useCallback(async () => {
    if (!publicacao) return;

    setCarregando(true);

    try {
      const { data } = await api.get(`/zernio/comments/${publicacao.id}`, {
        params: { accountId: publicacao.accountId }
      });

      setComentarios(data.data || []);
    } catch (err) {
      toastError(err);
      setComentarios([]);
    }

    setCarregando(false);
  }, [publicacao]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  if (!publicacao) return null;

  const responder = async (comentario, privado) => {
    const texto = (respostas[comentario.id] || "").trim();

    if (!texto) {
      toast.error("Escreva a resposta.");
      return;
    }

    setEnviando(`${comentario.id}-${privado ? "priv" : "pub"}`);

    try {
      await api.post(`/zernio/comments/${publicacao.id}/reply`, {
        accountId: publicacao.accountId,
        commentId: comentario.id,
        message: texto,
        privado
      });

      toast.success(
        privado ? "Resposta privada enviada." : "Resposta publicada."
      );

      setRespostas((atuais) => ({ ...atuais, [comentario.id]: "" }));
      await carregar();
    } catch (err) {
      toastError(err);
    }

    setEnviando(null);
  };

  return (
    <Dialog open onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            Comentários
            <SeloDeRede plataforma={publicacao.platform} />
            {publicacao.isAd && <Badge>Anúncio</Badge>}
          </DialogTitle>
          <DialogDescription className="line-clamp-2">
            {publicacao.content || "(publicação sem texto)"}
          </DialogDescription>
        </DialogHeader>

        {carregando ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : comentarios.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Sem comentários para mostrar.
          </p>
        ) : (
          <div className="-mx-1 flex-1 space-y-3 overflow-y-auto px-1">
            {comentarios.map((comentario) => (
              <div key={comentario.id} className="rounded-lg border p-3">
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {comentario.from?.name ||
                      comentario.from?.username ||
                      "Alguém"}
                  </span>
                  <span>{formatarData(comentario.createdTime)}</span>
                  {comentario.isHidden && (
                    <Badge variant="secondary">Oculto</Badge>
                  )}
                </div>

                <p className="mt-1 whitespace-pre-wrap break-words text-sm">
                  {comentario.message}
                </p>

                <Textarea
                  rows={2}
                  className="mt-2"
                  placeholder="Responder…"
                  value={respostas[comentario.id] || ""}
                  onChange={(e) =>
                    setRespostas((atuais) => ({
                      ...atuais,
                      [comentario.id]: e.target.value
                    }))
                  }
                />

                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => responder(comentario, false)}
                    disabled={enviando !== null}
                  >
                    {enviando === `${comentario.id}-pub` ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Reply className="h-4 w-4" />
                    )}
                    Responder no post
                  </Button>

                  <Button
                    size="sm"
                    onClick={() => responder(comentario, true)}
                    disabled={enviando !== null}
                  >
                    {enviando === `${comentario.id}-priv` ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Lock className="h-4 w-4" />
                    )}
                    Responder no privado
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Quadro = ({ publicacoes, onAbrir }) => (
  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
    {publicacoes.map((publicacao) => (
      <button
        key={publicacao.id}
        type="button"
        onClick={() => onAbrir(publicacao)}
        className="overflow-hidden rounded-xl border bg-card text-left transition-colors hover:border-foreground/20"
      >
        {/* A Zernio chama de `picture` a miniatura do post e de `permalink` o
            endereço público — não `mediaUrl`, que é o nome no inbox. */}
        {publicacao.picture ? (
          <img
            src={publicacao.picture}
            alt=""
            className="h-36 w-full bg-muted object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-36 w-full items-center justify-center bg-muted/50 text-muted-foreground">
            <ImageIcon className="h-6 w-6" />
          </div>
        )}

        <div className="space-y-2 p-3">
          <div className="flex items-center justify-between gap-2">
            <SeloDeRede plataforma={publicacao.platform} />
            <span className="inline-flex items-center gap-1 text-xs font-medium">
              <MessageSquare className="h-3 w-3" />
              {publicacao.commentCount ?? 0}
            </span>
          </div>

          <p className="line-clamp-3 text-sm">
            {publicacao.content || "(publicação sem texto)"}
          </p>

          {publicacao.isAd && <Badge variant="secondary">Anúncio</Badge>}
        </div>
      </button>
    ))}
  </div>
);

const Lista = ({ publicacoes, onAbrir }) => (
  <div className="rounded-xl border">
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Publicação</TableHead>
            <TableHead>Rede</TableHead>
            <TableHead>Conta</TableHead>
            <TableHead className="text-right">Comentários</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {publicacoes.map((publicacao) => (
            <TableRow
              key={publicacao.id}
              onClick={() => onAbrir(publicacao)}
              className="cursor-pointer"
            >
              <TableCell className="max-w-md">
                <p className="truncate text-sm">
                  {publicacao.content || "(publicação sem texto)"}
                </p>
              </TableCell>
              <TableCell>
                <SeloDeRede plataforma={publicacao.platform} />
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {publicacao.accountUsername}
                {publicacao.isAd && (
                  <Badge variant="secondary" className="ml-1">
                    Anúncio
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {publicacao.commentCount ?? 0}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
);

const Comentarios = () => {
  const [publicacoes, setPublicacoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [visao, setVisao] = useState("quadro");
  const [somenteComComentario, setSomenteComComentario] = useState(false);
  const [aberta, setAberta] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);

    try {
      const { data } = await api.get("/zernio/comments");
      setPublicacoes(data.data || []);
    } catch (err) {
      toastError(err);
    }

    setCarregando(false);
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const visiveis = useMemo(
    () =>
      somenteComComentario
        ? publicacoes.filter((item) => (item.commentCount ?? 0) > 0)
        : publicacoes,
    [publicacoes, somenteComComentario]
  );

  const comComentario = publicacoes.filter(
    (item) => (item.commentCount ?? 0) > 0
  ).length;

  return (
    <div className="space-y-4 p-4">
      <Cabecalho
        titulo="Comentários"
        descricao="O que comentaram nas suas publicações. Responder no post é público; responder no privado abre uma conversa com quem comentou."
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1">
          <Button
            variant={somenteComComentario ? "ghost" : "secondary"}
            size="sm"
            onClick={() => setSomenteComComentario(false)}
          >
            Todas
            <Badge variant="outline" className="ml-1 tabular-nums">
              {publicacoes.length}
            </Badge>
          </Button>
          <Button
            variant={somenteComComentario ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setSomenteComComentario(true)}
          >
            Com comentário
            <Badge variant="outline" className="ml-1 tabular-nums">
              {comComentario}
            </Badge>
          </Button>
        </div>

        <div className="flex items-center gap-1 rounded-lg border p-0.5">
          <Button
            variant={visao === "quadro" ? "secondary" : "ghost"}
            size="icon"
            className="h-7 w-7"
            onClick={() => setVisao("quadro")}
            aria-label="Ver em quadros"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={visao === "lista" ? "secondary" : "ghost"}
            size="icon"
            className="h-7 w-7"
            onClick={() => setVisao("lista")}
            aria-label="Ver em lista"
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {carregando ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : visiveis.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed px-6 py-12 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <MessageSquare className="h-5 w-5" />
          </span>
          <p className="mt-3 text-sm font-medium">Nada para responder</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            {publicacoes.length === 0
              ? "Nenhuma publicação nas contas conectadas. Instagram, Facebook, YouTube e TikTok aceitam comentário."
              : "Nenhuma publicação com comentário até agora."}
          </p>
        </div>
      ) : visao === "quadro" ? (
        <Quadro publicacoes={visiveis} onAbrir={setAberta} />
      ) : (
        <Lista publicacoes={visiveis} onAbrir={setAberta} />
      )}

      <ThreadModal publicacao={aberta} onClose={() => setAberta(null)} />
    </div>
  );
};

export default Comentarios;
